/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import DashboardFrameManager from '@ion/charting/src/bis/dashboard/DashboardFrameManager';
import {get} from '@ion/core/src/environment/environment';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {getLanguage} from '@ion/core/src/locale/locale';
import {error as errorLogger} from '@ion/core/src/logger/logger';
import Sisense, {DashboardId} from '@ion/core/src/services/Sisense';
import {AppState, LanguageIds, removeThemeInURL, SisenseState} from '@ion/core/src/services/SisenseUtils';
import {IFRAME_CONTAINER_NAME} from '@ion/dls/src/client/usePersistentSDKIFrame';
import useRootManager from '@ion/dls/src/client/useRootManager';
import {getSisenseTheme} from '@ion/dls/src/theme/resellers/Resellers';
import {useSystemOfUnits} from '../../hooks/useSystemOfUnits';
import {
  globalGroupIdSelector,
  globalGroupLoadingSelector
} from '../../store/selectors/global-group';
import {
  isTokenRegisteredSelector
} from '../../store/selectors/reports';
import {SisenseURLParams} from './useSisenseEmbedURL';
import useSisenseSelector from './useSisenseSelector';

// defining the default sisense settings...
export const DEFAULT_SETTINGS = {
  showLeftPane  : false,
  showToolbar   : false,
  showRightPane : true
};

// private method to remove the theme query param from the url...
const removeThemeFromURL = (container: HTMLElement, theme: string) => {
  const iFrameEle: any = container.firstChild;
  const url            = iFrameEle?.src;
  const resolved       = (url && removeThemeInURL(url, theme)) || '';
  if (url && url !== resolved) {
    iFrameEle.src = resolved;
  }
};

/**
 * The hook is responsible for handling the session, keep alive,and
 * refreshing the JWT before creating sisense frame by SDK(Sisense embed type)
 * to the functional component which is calling this.
 *
 * ```typescript
 *
 *  import {useSisenseSDK} from '../../hooks/';
 *
 *  const SomeComponent = () => {
 *   const {
 *    url,
 *    loading,
 *    hasError: error,
 *    onRefresh
 *   } = useSisenseSDK({dashboardId: '61ae95d512b1a20037dbb6bd'});
 *  }
 *
 * ```
 */
export const useSisenseSDK = ({
  dashboardId,
  filter,
  iFrameId,
  onHomeView,
  onSavedReportLoad,
  isEditMode = false,
  queryParams = '',
  widgetId = null,
  settings = DEFAULT_SETTINGS,
  clearThemeInDefault = false,
  persistIFrame = false
}: SisenseURLParams) => {
  const {SisenseFrame, enums} = window['sisense.embed'];
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(false);
  const filterRef             = useRef(filter);
  const prevAppStateRef       = useRef(dashboardId ? AppState.Dashboard : null);
  const isMounted             = useHasMounted();
  const globalGroup           = useSelector(globalGroupIdSelector);
  const isGGCLoading          = useSelector(globalGroupLoadingSelector);
  const uom                   = useSystemOfUnits();
  const isTokenRegistered     = useSelector(isTokenRegisteredSelector);
  const containerId           = widgetId || dashboardId;
  const lang                  = LanguageIds[getLanguage()];
  const sisenseOptionsError   = useSisenseSelector(SisenseState.SisenseOptionsError) as boolean;
  const frameContainerId      = iFrameId ?? containerId;

  const [loadedId, setLoadedId] = useState(null);

  const {
    createContainer
  } = useRootManager(false);

  // clearing the timeout during unmount...
  useEffect(() => {
    return () => {
      Sisense.stopSessionRefresh();
    };
  }, []);

  // clearing the timeout during unmount...
  useEffect(() => {
    if (DashboardFrameManager.getFrameInstance(frameContainerId)) {
      Sisense.startSessionRefresh();
    }
  }, []);

  useEffect(() => {
    filterRef.current = filter;
    const frameRef = DashboardFrameManager.getFrameInstance(frameContainerId);
    if (frameRef && filter) {
      frameRef.dashboard?.applyFilters?.(filter);
    }
  }, [filter]);

  useEffect(() => {
    const frameEle = document.querySelector(`iframe[id="${frameContainerId}"]`);
    const frameRef = DashboardFrameManager.getFrameInstance(frameContainerId);
    const existingId = frameRef?.dashboard?.iframeClient?._cid;
    if (dashboardId && frameEle && frameRef?.dashboard && existingId !== dashboardId) {
      frameRef.dashboard.open(dashboardId, true);
    }
    if (dashboardId && frameRef && dashboardId !== DashboardId.InitialDashboard) {
      frameRef.app?.on?.(enums.ApplicationEventType.STATECHANGED, onStateChange);
    } else if (dashboardId === DashboardId.InitialDashboard && frameRef?.app._events.stateappchanged) {
      frameRef.app?.off?.(enums.ApplicationEventType.STATECHANGED, onStateChange);
    }
  }, [dashboardId, frameContainerId, loadedId]);

  useEffect(() => {
    setError(sisenseOptionsError);
  }, [sisenseOptionsError]);

  const onDashboardLoaded = useCallback(({dashboard}) => {
    const frameRef = DashboardFrameManager.getFrameInstance(frameContainerId);
    frameRef.updateSettings(settings);
    if (filterRef.current && frameRef) {
      frameRef?.dashboard?.applyFilters?.(filterRef.current);
      clearThemeInDefault && frameRef?.app?.clearTheme?.();
    }
    if (dashboard?.instanceType === 'owner') {
      onSavedReportLoad?.(dashboard);
    }
    setLoadedId(dashboard?.oid || DashboardId.InitialDashboard);
  }, [clearThemeInDefault, frameContainerId, onSavedReportLoad]);

  const onStateChange = useCallback(({stateName}) => {
    const hasChanged = prevAppStateRef.current !== AppState.Home && prevAppStateRef.current !== stateName;
    const frameRef = DashboardFrameManager.getFrameInstance(frameContainerId);
    if (isEditMode && hasChanged && (stateName === AppState.Dashboard || stateName === AppState.WidgetEdit)) {
      frameRef.updateSettings({
        ...settings,
        showLeftPane : (
          stateName === AppState.WidgetEdit ? true
            : settings.showLeftPane
        ),
        showToolbar : (
          stateName === AppState.WidgetEdit ? true
            : settings.showToolbar
        )
      });
    } else if (stateName === AppState.Home) {
      frameRef.updateSettings({
        showRightPane : false,
        showToolbar   : false,
        showLeftPane  : false
      });
      onHomeView?.();
    }
    prevAppStateRef.current = stateName;
  }, [settings, frameContainerId, onHomeView]);

  useEffect(() => {
    if (isMounted && isTokenRegistered && globalGroup && !isGGCLoading && containerId) {
      const loadFrame = async function() {
        setLoading(true);
        setError(false);
        try {
          // just to call for maintaining the session
          await Sisense.getEmbedURL({dashboardId, widgetId});
          let container       = null;
          const serverOptions = Sisense.getServerOptions();
          if (persistIFrame) {
            container = createContainer(frameContainerId);
            container.style.display = 'none';
            container.setAttribute('name', IFRAME_CONTAINER_NAME);
            document.body.appendChild(container);
          } else {
            container = document.getElementById(frameContainerId);
          }
          const hasError = !(serverOptions && container);
          setError(hasError);
          if (!hasError) {
            const frameEle = container.childNodes[0];
            // remove if we have already auto generate iframe...
            if (frameEle) {
              container.removeChild(frameEle);
            }
            const theme = getSisenseTheme(serverOptions.themeId as string);
            // creating a new Sisense iframe...
            const sisenseFrame = new SisenseFrame({
              editMode  : isEditMode,
              url       : get('sisenseBaseURL'),
              dashboard : dashboardId,
              widget    : widgetId,
              id        : frameContainerId,
              // if we want to pass any query params other than init,
              // then we can only pass by merging with theme param...
              theme     : `${theme}&locale=${lang}${queryParams}`,
              settings
            });
            // render the dashboard/widget in auto generated iframe...
            await sisenseFrame.render(container);
            clearThemeInDefault && removeThemeFromURL(container, theme);
            DashboardFrameManager.setFrameInstance(frameContainerId, sisenseFrame);
            sisenseFrame.dashboard?.on(enums.DashboardEventType.LOADED, onDashboardLoaded);
          }
        } catch (err) {
          errorLogger(err);
          setError(true);
        }
        setLoading(false);
      };
      if (Sisense.isPreInitialized()) {
        const frameEle = document.querySelector(`iframe[id="${frameContainerId}"]`);
        if (!persistIFrame || !frameEle) {
          loadFrame();
        }
      } else {
        setError(true);
        errorLogger('sisense/frame.js script is not found');
      }
    }
  }, [
    containerId,
    persistIFrame,
    isTokenRegistered,
    globalGroup,
    isGGCLoading,
    isMounted,
    uom,
    refresh,
    frameContainerId
  ]);

  const onRefresh = () => {
    setRefresh(!refresh);
  };

  return {
    ref : DashboardFrameManager.getFrameInstance(frameContainerId),
    loading,
    error,
    onRefresh
  };
};

export default useSisenseSDK;
